/**
 * Navbar component for vertical layout.
 * Modern floating glass navbar with backdrop blur.
 */

// MUI Imports
import { useTheme } from '@mui/material/styles';
import useScrollTrigger from '@mui/material/useScrollTrigger';

// Third-party Imports
import classnames from 'classnames';
import type { CSSObject } from '@emotion/styled';

// Type Imports
import type { ChildrenType } from '@core/types';

// Config Imports
import themeConfig from '@configs/themeConfig';

// Hook Imports
import { useSettings } from '@core/hooks/useSettings';

// Component Imports
import { TenantSwitcher } from '../../../components/layout/TenantSwitcher';
import { ProjectSwitcher } from '../../../components/layout/ProjectSwitcher';
import { ThemeSwitcher } from '../../../components/common/ThemeSwitcher';
import { UserMenu } from '../../../components/layout/UserMenu';

// Util Imports
import { verticalLayoutClasses } from '@layouts/utils/layoutClasses';

// Styled Component Imports
import StyledHeader from '@layouts/styles/vertical/StyledHeader';

// MUI Components
import { Box, Typography } from '@mui/material';
import { useAuthStore } from '../../../store/authStore';

type Props = ChildrenType & {
  overrideStyles?: CSSObject;
};

/**
 * Navbar component with glassmorphism design
 */
const Navbar = (props: Props) => {
  const { children, overrideStyles } = props;
  const { settings } = useSettings();
  const theme = useTheme();
  const { isAuthenticated } = useAuthStore();

  const trigger = useScrollTrigger({
    threshold: 0,
    disableHysteresis: true,
  });

  const { navbarContentWidth } = settings;

  const headerFixed = themeConfig.navbar.type === 'fixed';
  const headerStatic = themeConfig.navbar.type === 'static';
  const headerFloating = themeConfig.navbar.floating === true;
  const headerDetached = themeConfig.navbar.detached === true;
  const headerAttached = themeConfig.navbar.detached === false;
  const headerBlur = themeConfig.navbar.blur === true;
  const headerContentCompact = navbarContentWidth === 'compact';
  const headerContentWide = navbarContentWidth === 'wide';

  return (
    <StyledHeader
      theme={theme}
      overrideStyles={overrideStyles}
      className={classnames(verticalLayoutClasses.header, {
        [verticalLayoutClasses.headerFixed]: headerFixed,
        [verticalLayoutClasses.headerStatic]: headerStatic,
        [verticalLayoutClasses.headerFloating]: headerFloating,
        [verticalLayoutClasses.headerDetached]: !headerFloating && headerDetached,
        [verticalLayoutClasses.headerAttached]: !headerFloating && headerAttached,
        [verticalLayoutClasses.headerBlur]: headerBlur,
        [verticalLayoutClasses.headerContentCompact]: headerContentCompact,
        [verticalLayoutClasses.headerContentWide]: headerContentWide,
        scrolled: trigger,
      })}
    >
      <div className={classnames(verticalLayoutClasses.navbar, 'flex bs-full')}>
        {children || (
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: 'var(--mui-palette-text-disabled)',
                  fontWeight: 400,
                }}
              >
                Welcome to CoOrdio Cortex
              </Typography>
            </Box>
            {isAuthenticated && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mr: 2, flexWrap: 'wrap' }}>
                <TenantSwitcher />
                <ProjectSwitcher />
              </Box>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ThemeSwitcher />
              <UserMenu />
            </Box>
          </Box>
        )}
      </div>
    </StyledHeader>
  );
};

export default Navbar;
